import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import './accounting.css';

const BankAccounts = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [bankFilter, setBankFilter] = useState('');
  const [banks, setBanks] = useState([]);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    fetchBankAccounts();
    fetchBanks();
    fetchStatistics();
  }, []);

  const fetchBankAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://naql.nozzm.com/api/bank-accounts', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setBankAccounts(data.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/banks/active', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setBanks(data || []);
    } catch (err) {
      console.error('Error fetching banks:', err);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/bank-accounts/statistics', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setStatistics(data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const handleToggleStatus = async (accountId) => {
    try {
      const response = await fetch(`https://naql.nozzm.com/api/bank-accounts/${accountId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      fetchBankAccounts();
      fetchStatistics();
    } catch (err) {
      setError(err.message || 'حدث خطأ في تغيير الحالة');
    }
  };

  const handleSetDefault = async (accountId) => {
    try {
      const response = await fetch(`https://naql.nozzm.com/api/bank-accounts/${accountId}/set-default`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      fetchBankAccounts();
      fetchStatistics();
    } catch (err) {
      setError(err.message || 'حدث خطأ في تعيين الحساب الافتراضي');
    }
  };

  const handleDelete = async (accountId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الحساب البنكي؟')) {
      try {
        const response = await fetch(`https://naql.nozzm.com/api/bank-accounts/${accountId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'lang': i18n.language,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        fetchBankAccounts();
        fetchStatistics();
      } catch (err) {
        setError(err.message || 'حدث خطأ في حذف الحساب البنكي');
      }
    }
  };

  const filteredBankAccounts = bankAccounts.filter(account => {
    const matchesSearch = account.account_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         account.account_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || account.status === statusFilter;
    const matchesBank = !bankFilter || account.bank_id === parseInt(bankFilter);
    return matchesSearch && matchesStatus && matchesBank;
  });

  const getStatusBadge = (status) => {
    const statusClasses = {
      'active': 'bg-success',
      'inactive': 'bg-secondary',
      'closed': 'bg-danger',
      'suspended': 'bg-warning'
    };
    return (
      <span className={`badge ${statusClasses[status] || 'bg-secondary'}`}>
        {t(`accounting.bankAccounts.accountStatus.${status}`)}
      </span>
    );
  };

  const getAccountTypeBadge = (type) => {
    return (
      <span className="badge bg-info">
        {t(`accounting.bankAccounts.accountTypes.${type}`)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bank-accounts-container">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bank-accounts-container">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bank-accounts-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h2>{t('accounting.bankAccounts.title')}</h2>
          <p>إدارة الحسابات البنكية</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/bank-accounts/add')}
          >
            {t('accounting.bankAccounts.actions.add')}
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="statistics-cards">
        <div className="stat-card">
          <div className="stat-icon">💳</div>
          <div className="stat-content">
            <h3>{statistics?.total_accounts || 0}</h3>
            <p>{t('accounting.bankAccounts.statistics.totalAccounts')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{statistics?.active_accounts || 0}</h3>
            <p>{t('accounting.bankAccounts.statistics.activeAccounts')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>{statistics?.total_balance ? `${statistics.total_balance.toLocaleString()} ريال` : '0 ريال'}</h3>
            <p>{t('accounting.bankAccounts.statistics.totalBalance')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <h3>{statistics?.default_account ? '1' : '0'}</h3>
            <p>{t('accounting.bankAccounts.statistics.defaultAccount')}</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="row">
          <div className="col-md-4">
            <div className="search-box">
              <input
                type="text"
                className="form-control"
                placeholder="البحث في الحسابات البنكية..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">جميع الحالات</option>
              <option value="active">نشط</option>
              <option value="inactive">غير نشط</option>
              <option value="closed">مغلق</option>
              <option value="suspended">معلق</option>
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={bankFilter}
              onChange={(e) => setBankFilter(e.target.value)}
            >
              <option value="">جميع البنوك</option>
              {banks.map(bank => (
                <option key={bank.id} value={bank.id}>
                  {bank.bank_name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <button 
              className="btn btn-outline-secondary w-100"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setBankFilter('');
              }}
            >
              مسح الفلاتر
            </button>
          </div>
        </div>
      </div>

      {/* Bank Accounts Table */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>{t('accounting.bankAccounts.accountNumber')}</th>
                <th>{t('accounting.bankAccounts.accountName')}</th>
                <th>{t('accounting.bankAccounts.bank')}</th>
                <th>{t('accounting.bankAccounts.accountType')}</th>
                <th>{t('accounting.bankAccounts.currentBalance')}</th>
                <th>{t('accounting.bankAccounts.status')}</th>
                <th>{t('accounting.bankAccounts.actions.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredBankAccounts.map((account) => (
                <tr key={account.id}>
                  <td>{account.account_number}</td>
                  <td>{account.account_name}</td>
                  <td>{account.bank?.bank_name}</td>
                  <td>{getAccountTypeBadge(account.account_type)}</td>
                  <td>{account.current_balance ? `${account.current_balance.toLocaleString()} ريال` : '0 ريال'}</td>
                  <td>{getStatusBadge(account.status)}</td>
                  <td>
                    <div className="btn-group" role="group">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/bank-accounts/view/${account.id}`)}
                        title={t('accounting.bankAccounts.actions.view')}
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => navigate(`/bank-accounts/edit/${account.id}`)}
                        title={t('accounting.bankAccounts.actions.edit')}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => handleToggleStatus(account.id)}
                        title={t('accounting.bankAccounts.actions.toggleStatus')}
                      >
                        <i className={`fas fa-${account.status === 'active' ? 'pause' : 'play'}`}></i>
                      </button>
                      {!account.is_default && (
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => handleSetDefault(account.id)}
                          title={t('accounting.bankAccounts.actions.setDefault')}
                        >
                          <i className="fas fa-star"></i>
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(account.id)}
                        title={t('accounting.bankAccounts.actions.delete')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredBankAccounts.length === 0 && (
          <div className="text-center py-4">
            <p className="text-muted">لا توجد حسابات بنكية لعرضها</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankAccounts;
